import type { VideoCarouselBannerModel } from "src/features/HomepageCarouselBanners/models";
import type { ReactNode } from "react";

import { Video, PlayCircle, Loader2 } from "lucide-react";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";

export interface VideoCarouselBannerProps extends Partial<VideoCarouselBannerModel> {
	showPlaceholder?: boolean;

	showGradient?: boolean;
}

const VideoCarouselBanner = ({
	videoUuid,
	headingText,
	subheadingText,
	showPlaceholder = true,
	showGradient = false
}: VideoCarouselBannerProps) => {
	const {
		url: videoUrl,
		thumbnail,
		processingStatus
	} = useAzureVideoDownload("video", "", videoUuid);

	const renderPlaceholder = (children: ReactNode) => {
		if (!showPlaceholder) return null;

		return children;
	};

	const background =
		showGradient || headingText || subheadingText
			? "linear-gradient(0deg, #000 -38.1%, rgba(0, 0, 0, 0.00) 100%)"
			: undefined;

	return (
		<div
			className="relative bg-border w-full flex justify-center items-center"
			style={{ aspectRatio: 16 / 9 }}
		>
			{videoUrl ? (
				<video
					src={videoUrl}
					poster={thumbnail || undefined}
					controls
					className="w-full h-full object-cover"
					preload="metadata"
				/>
			) : thumbnail ? (
				<div
					className="w-full h-full flex justify-center items-center bg-no-repeat bg-cover"
					style={{
						background: `url(${thumbnail})`,
						backgroundRepeat: "no-repeat",
						backgroundSize: "cover"
					}}
				>
					{processingStatus === "Processing" || !videoUrl ? (
						<div className="flex flex-col items-center justify-center gap-2">
							<Loader2 className="h-8 w-8 text-white/70 animate-spin" />
							<span className="text-sm text-white/50">Processing video...</span>
						</div>
					) : (
						<PlayCircle size={40} color="#000000" />
					)}
				</div>
			) : videoUuid ? (
				<div className="flex flex-col items-center justify-center gap-2 w-full h-full">
					<Loader2 className="h-8 w-8 text-white/70 animate-spin" />
					<span className="text-sm text-white/50">Processing video...</span>
				</div>
			) : (
				renderPlaceholder(<Video size={40} opacity={0.2} color="#000000" />)
			)}

			<div
				className="absolute w-full bottom-0 px-6 pt-4 pb-6 flex flex-col justify-end"
				style={{ background }}
			>
				{headingText ? (
					<p className="text-white font-bold text-2xl leading-tight">
						{headingText}
					</p>
				) : (
					renderPlaceholder(
						<div
							className="w-1/2 h-[5px] bg-white rounded-[65px] mb-[5px]"
							data-testid="heading-text-placeholder"
						/>
					)
				)}

				{subheadingText ? (
					<p className="text-white text-base mt-1 opacity-80">
						{subheadingText}
					</p>
				) : (
					renderPlaceholder(
						<div
							className="w-1/4 h-[2px] bg-white rounded-[65px]"
							data-testid="subheading-text-placeholder"
						/>
					)
				)}
			</div>
		</div>
	);
};

export default VideoCarouselBanner;
